import {
  PATH_ACCEPT_INVITE,
  PATH_AUTOMATIONS,
  PATH_CALENDAR,
  PATH_CALL_CENTER,
  PATH_CALL_CENTER_CALL_LEAD,
  PATH_CALL_CENTER_CLOUD_RECORDING_DETAILS,
  PATH_CALL_CENTER_CLOUD_RECORDINGS,
  PATH_CALL_CENTER_COMPANY_ROOM,
  PATH_CALL_CENTER_EOD_REPORT,
  PATH_CALL_CENTER_JOIN,
  PATH_CALL_CENTER_JOIN_AS_ADMIN,
  PATH_CALL_CENTER_PERSONAL_ROOM_RECORDINGS,
  PATH_CALL_CENTER_WRAP_UP,
  PATH_DASHBOARD,
  PATH_DEAL_DETAILS,
  PATH_FORGOT_PASSWORD,
  PATH_FORM_BUILDER,
  PATH_FORMS,
  PATH_FUNNELS,
  PATH_GETTING_STARTED,
  PATH_INBOX,
  PATH_LEAD_DETAILS,
  PATH_LEADS,
  PATH_LOGIN,
  PATH_MANAGE_MEETING_SCHEDULER,
  PATH_MEETING_SCHEDULER,
  PATH_ONBOARDING,
  PATH_PIPELINES,
  PATH_PIPELINE_VIEWS,
  PATH_PIPELINE_VIEW,
  PATH_PRIVACY_POLICY,
  PATH_RESET_PASSWORD,
  PATH_SETTINGS,
  PATH_SETTINGS_AVAILABILITY,
  PATH_SETTINGS_BILLING,
  PATH_SETTINGS_CALL_CENTER,
  PATH_SETTINGS_DOMAIN_CONNECT,
  PATH_SETTINGS_DOMAINS,
  PATH_SETTINGS_EMAIL,
  PATH_SETTINGS_EMAIL_TEMPLATES,
  PATH_SETTINGS_FIELDS,
  PATH_SETTINGS_INTEGRATIONS,
  PATH_SETTINGS_INTEGRATIONS_GOOGLE_ADS,
  PATH_SETTINGS_INTEGRATIONS_HUBSPOT,
  PATH_SETTINGS_INTEGRATIONS_META,
  PATH_SETTINGS_INTEGRATIONS_SLACK,
  PATH_SETTINGS_PHONE_NUMBERS,
  PATH_SETTINGS_PIXEL_INSTALL,
  PATH_SETTINGS_PROFILE,
  PATH_SETTINGS_SNIPPETS,
  PATH_SETTINGS_TRAINING,
  PATH_SETTINGS_USER_MANAGEMENT,
  PATH_SETUP_CALENDAR,
  PATH_SIGNUP,
  PATH_SUPPORT,
  PATH_TASKS,
  PATH_TRAINING,
  PATH_TRAINING_AGENT_PROFILE,
  PATH_TRAINING_WEEKLY_REPORT,
} from "@/routes/paths";
import { IS_PRODUCTION } from "@/config";

/**
 * Static browser-tab title per route ("<title> · IronPipeline"). Matching
 * uses react-router's ranking, so the most specific path wins regardless of
 * the order below. Unlisted routes fall back to "IronPipeline".
 *
 * Adding a screen: one line here. If the tab should name a record (a lead, a
 * deal…), also call `usePageTitle(name)` in the screen — the entry here is
 * what the tab shows while that data loads.
 */
export const PAGE_TITLES: { path: string; title: string }[] = [
  { path: PATH_DASHBOARD, title: "Dashboard" },

  // CRM
  { path: PATH_LEADS, title: "Leads" },
  { path: PATH_LEAD_DETAILS, title: "Lead" },
  { path: PATH_PIPELINES, title: "Pipelines" },
  { path: PATH_DEAL_DETAILS, title: "Deal" },
  { path: PATH_PIPELINE_VIEWS, title: "Pipeline views" },
  { path: PATH_PIPELINE_VIEW, title: "Pipeline view" },
  { path: PATH_TASKS, title: "Tasks" },
  { path: PATH_CALENDAR, title: "Calendar" },
  { path: PATH_INBOX, title: "Inbox" },

  // Acquire / automate
  { path: PATH_FUNNELS, title: "Funnels" },
  { path: PATH_FORMS, title: "Forms" },
  { path: PATH_FORM_BUILDER, title: "Form" },
  // The Automations route doesn't exist in production builds (see
  // routes.config.ts); skip the title too so its 404 doesn't name the
  // unreleased module.
  ...(IS_PRODUCTION ? [] : [{ path: PATH_AUTOMATIONS, title: "Automations" }]),
  { path: PATH_MEETING_SCHEDULER, title: "Meetings" },
  { path: PATH_MANAGE_MEETING_SCHEDULER, title: "Manage schedulers" },

  // Call center
  { path: PATH_CALL_CENTER, title: "Call Center" },
  { path: PATH_CALL_CENTER_JOIN, title: "In call" },
  { path: PATH_CALL_CENTER_JOIN_AS_ADMIN, title: "Monitoring call" },
  { path: PATH_CALL_CENTER_CALL_LEAD, title: "Phone call" },
  { path: PATH_CALL_CENTER_WRAP_UP, title: "Call wrap-up" },
  { path: PATH_CALL_CENTER_EOD_REPORT, title: "EOD Report" },
  { path: PATH_CALL_CENTER_CLOUD_RECORDINGS, title: "Recordings" },
  { path: PATH_CALL_CENTER_CLOUD_RECORDING_DETAILS, title: "Recording" },
  { path: PATH_CALL_CENTER_PERSONAL_ROOM_RECORDINGS, title: "Personal Rooms" },
  { path: PATH_CALL_CENTER_COMPANY_ROOM, title: "Company Room" },

  // Training
  { path: PATH_TRAINING, title: "Training" },
  { path: PATH_TRAINING_WEEKLY_REPORT, title: "Weekly report" },
  { path: PATH_TRAINING_AGENT_PROFILE, title: "Agent profile" },

  // Settings
  { path: PATH_SETTINGS, title: "Settings" },
  { path: PATH_SETTINGS_PROFILE, title: "Profile · Settings" },
  { path: PATH_SETTINGS_AVAILABILITY, title: "Availability · Settings" },
  { path: PATH_SETTINGS_USER_MANAGEMENT, title: "Users · Settings" },
  { path: PATH_SETTINGS_INTEGRATIONS, title: "Integrations · Settings" },
  { path: PATH_SETTINGS_INTEGRATIONS_HUBSPOT, title: "HubSpot · Settings" },
  { path: PATH_SETTINGS_INTEGRATIONS_META, title: "Meta · Settings" },
  { path: PATH_SETTINGS_INTEGRATIONS_GOOGLE_ADS, title: "Google Ads · Settings" },
  { path: PATH_SETTINGS_INTEGRATIONS_SLACK, title: "Slack · Settings" },
  { path: PATH_SETTINGS_BILLING, title: "Billing · Settings" },
  { path: PATH_SETTINGS_PIXEL_INSTALL, title: "Pixel · Settings" },
  { path: PATH_SETTINGS_DOMAINS, title: "Domains · Settings" },
  { path: PATH_SETTINGS_DOMAIN_CONNECT, title: "Connect domain · Settings" },
  { path: PATH_SETTINGS_FIELDS, title: "Fields · Settings" },
  { path: PATH_SETTINGS_CALL_CENTER, title: "Call Center · Settings" },
  { path: PATH_SETTINGS_PHONE_NUMBERS, title: "Phone numbers · Settings" },
  { path: PATH_SETTINGS_TRAINING, title: "Training · Settings" },
  { path: PATH_SETTINGS_EMAIL, title: "Email · Settings" },
  { path: PATH_SETTINGS_EMAIL_TEMPLATES, title: "Email templates · Settings" },
  { path: PATH_SETTINGS_SNIPPETS, title: "Snippets · Settings" },

  // Onboarding / misc
  { path: PATH_GETTING_STARTED, title: "Getting started" },
  { path: PATH_SETUP_CALENDAR, title: "Set up calendar" },
  { path: PATH_ONBOARDING, title: "Onboarding" },
  { path: PATH_SUPPORT, title: "Support" },
  { path: PATH_PRIVACY_POLICY, title: "Privacy Policy" },

  // Auth
  { path: PATH_LOGIN, title: "Log in" },
  { path: PATH_SIGNUP, title: "Sign up" },
  { path: PATH_FORGOT_PASSWORD, title: "Forgot password" },
  { path: PATH_RESET_PASSWORD, title: "Reset password" },
  { path: PATH_ACCEPT_INVITE, title: "Accept invite" },
];
